/* eslint-disable no-unused-vars */
// InterviewManagementScreen_Integrated.js
// Fully integrated, productive, and functional interview management system
// Production-ready with comprehensive features and business integration

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  FlatList,
  ScrollView,
  Modal,
  TextInput,
  ActivityIndicator,
  RefreshControl,
  Alert,
  Share,
  Dimensions,
  StatusBar,
  Platform,
  Image,
  Switch,
  KeyboardAvoidingView,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
  AntDesign,
  MaterialIcons,
  Feather,
} from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

const { width, height } = Dimensions.get('window');

const API_BASE_URL = 'https://api.jobeng.ai';
const API_ENDPOINTS = {
  companyProfile: `${API_BASE_URL}/api/data/company_profiles`,
  // application_interviews is the canonical table — same row the candidate's
  // VideoInterviewScreen reads from. The old /api/data/interviews path never
  // existed and silently 404'd into the AsyncStorage fallback.
  interviews:     `${API_BASE_URL}/api/data/application_interviews`,
  // Reads — the recruiter list view uses the JOINed endpoint so candidate
  // name + job title come in pre-resolved without N+1 fetches.
  interviewsList: `${API_BASE_URL}/api/recruitment/interviews`,
  applications:   `${API_BASE_URL}/api/data/applications`,
  jobs:           `${API_BASE_URL}/api/data/jobs`,
  offers:         `${API_BASE_URL}/api/data/job_offers`,
  scheduleStage:  (id) => `${API_BASE_URL}/api/recruitment/applicant/${id}/stage`,
};
const api = axios.create({
  baseURL: API_BASE_URL, timeout: 15000,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef' },
});
api.interceptors.request.use(async (config) => {
  try {
    const token = await AsyncStorage.getItem('auth_token');
    if (token) config.headers.Authorization = 'Bearer ' + token;
    const companyId = await AsyncStorage.getItem('company_id');
    if (companyId) config.params = { ...config.params, company_id: parseInt(companyId) };
  } catch {} return config;
}, (error) => Promise.reject(error));
const safeData = r => {
  if (!r) return [];
  const body = r.data ?? r;
  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.data)) return body.data;
  if (Array.isArray(body?.rows)) return body.rows;
  if (Array.isArray(body?.results)) return body.results;
  if (Array.isArray(body?.interviews)) return body.interviews;
  return [];
};

// Maps interview row.status → header pill colour. Kept here (not inside
// the component) so loadInterviews can reach it without prop-drilling.
const STATUS_COLOR = {
  scheduled:   '#4A90E2',
  in_progress: '#F59E0B',
  completed:   '#10B981',
  cancelled:   '#EF4444',
  rescheduled: '#8B5CF6',
  no_show:     '#9CA3AF',
};

const InterviewManagementScreen_Integrated = ({ navigation, route }) => {
  // State management
  const jobId = route?.params?.jobId || null;
  const jobTitle = route?.params?.jobTitle || '';
  // Optional: scope this screen to ONE candidate's interviews. Set by
  // ScheduleInterview/VideoScreen after they save so the recruiter lands in
  // a focused view of that candidate's interviews instead of the whole
  // company's list. When null we show everything.
  const focusApplicantId = route?.params?.applicantId
                        || route?.params?.applicationId
                        || null;
  const focusApplicantName = route?.params?.applicantName
                          || route?.params?.candidate?.name
                          || '';
  const [companyLogo, setCompanyLogo] = useState(route?.params?.companyLogo || null);
  const [companyName, setCompanyName] = useState(route?.params?.companyName || '');
  const [companyId, setCompanyId] = useState(null);
  const [interviews, setInterviews] = useState([]);
  const [applicants, setApplicants] = useState([]);
  const [interviewers, setInterviewers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('Scheduled');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('All');
  const [sortBy, setSortBy] = useState('date');
  
  // Modal states
  const [showInterviewModal, setShowInterviewModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  // ── Schedule Interview Form ──
  const [schedForm, setSchedForm] = useState({ applicant_name: '', applicant_email: '', position: '', type: 'Technical', date: '', time: '', duration: '60', location: 'Video Call', interviewers: '', notes: '', preparation: '', panel_size: '1' });
  const INTERVIEW_TYPES = ['Technical', 'Behavioral', 'Panel', 'Phone Screen', 'Video', 'Take Home', 'Final', 'HR', 'Culture Fit', 'Whiteboard'];
  const LOCATIONS = ['Video Call', 'Office', 'Phone', 'Remote', 'On-Site', 'Conference Room A', 'Conference Room B', 'Meeting Room 1'];
  const DURATIONS = ['15', '30', '45', '60', '90', '120'];

  // ── Reschedule Form ──
  const [reschedForm, setReschedForm] = useState({ interview_id: '', new_date: '', new_time: '', reason: '', notify_applicant: true, notify_interviewers: true });

  // ── Final Interview Form ──
  const [finalForm, setFinalForm] = useState({ applicant_name: '', position: '', date: '', time: '', duration: '90', location: 'Office', panel: '', presentation_topic: '', assessment_criteria: '', dress_code: 'Business Professional', offer_ready: false });

  // ── First Day Form ──
  const [firstDayForm, setFirstDayForm] = useState({ employee_name: '', start_date: '', start_time: '09:00', location: 'Office', manager: '', buddy: '', equipment: '', welcome_message: '', agenda: '', special_requirements: '' });
  const EQUIPMENT_OPTIONS = ['Laptop', 'Monitor', 'Keyboard', 'Mouse', 'Headset', 'Phone', 'Access Card', 'Parking Pass', 'Desk Setup'];
  const [selectedEquipment, setSelectedEquipment] = useState([]);

  // ── Settings Form ──
  const [settingsForm, setSettingsForm] = useState({ working_hours_start: '09:00', working_hours_end: '18:00', buffer_time: '15', max_interviews_per_day: '8', auto_reminders: true, reminder_hours_before: '24', default_duration: '60', default_location: 'Video Call', allow_self_schedule: false, timezone: 'Europe/London' });
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackScore, setFeedbackScore] = useState('');
  const [scheduleForm, setScheduleForm] = useState({ applicantName: '', position: '', type: 'technical', date: '', time: '', duration: '45', format: 'Video', notes: '' });
  const [selectedInterview, setSelectedInterview] = useState(null);
  
  // Statistics
  const [statistics, setStatistics] = useState({
    total: 0,
    scheduled: 0,
    completed: 0,
    cancelled: 0,
    today: 0,
    thisWeek: 0,
  });

  // Interview types
  const interviewTypes = useMemo(() => [
    { id: 'technical', name: 'Technical', icon: 'code-braces', color: '#3B82F6' },
    { id: 'behavioral', name: 'Behavioral', icon: 'account-heart', color: '#10B981' },
    { id: 'cultural', name: 'Cultural Fit', icon: 'account-group', color: '#8B5CF6' },
    { id: 'final', name: 'Final', icon: 'account-tie', color: '#EF4444' },
    { id: 'screening', name: 'Screening', icon: 'phone', color: '#F59E0B' },
  ], []);

  // Load data on component mount
  useFocusEffect(React.useCallback(() => { loadCompanyLogo(); }, []));
  const loadCompanyLogo = async () => {
    try {
      // 1. Cache hit for instant paint
      const cachedProfile = await AsyncStorage.getItem('company_profile');
      if (cachedProfile) {
        const profile = JSON.parse(cachedProfile);
        setCompanyLogo(profile.logo || null);
        if (profile.name || profile.company_name) setCompanyName(profile.name || profile.company_name);
      }
      // 2. Refresh from /api/company-profile/:id so updates elsewhere flow through.
      const cid = await AsyncStorage.getItem('company_id');
      if (cid) {
        try {
          const res = await fetch(`https://api.jobeng.ai/api/company-profile/${cid}`, {
            headers: { 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef' }
          });
          if (res.ok) {
            const json = await res.json();
            const row = (json?.data && !Array.isArray(json.data)) ? json.data
                     : (Array.isArray(json?.data) ? json.data[0] : json);
            if (row && (row.logo || row.name || row.company_name)) {
              if (row.logo) setCompanyLogo(row.logo);
              const nm = row.name || row.company_name;
              if (nm) setCompanyName(nm);
              await AsyncStorage.setItem('company_profile', JSON.stringify(row));
              console.log('[CompanyLogo] refreshed from API for cid=' + cid);
            }
          }
        } catch (e) { /* offline — cache is enough */ }
      }
    } catch (e) { console.warn('Logo load error:', e.message); }
  };

  useEffect(() => {
    loadAllData();
  }, []);

  // Calculate statistics when data changes
  useEffect(() => {
    calculateStatistics();
  }, [interviews]);

  // Load all data
  const loadAllData = async () => {
    try {
      setIsLoading(true);
      await Promise.all([
        loadInterviews(),
        loadApplicants(),
        loadInterviewers(),
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Load interviews
  const loadInterviews = async () => {
    try {
      const cid = companyId || parseInt(await AsyncStorage.getItem('company_id') || '0', 10);
      if (!cid) { setInterviews([]); return; }
      // Use the JOINed recruitment endpoint — pre-resolves candidate_name from
      // users.full_name, job_title from jobs, employer_name from company_profiles.
      // The previous path (/api/data/interviews) didn't exist and silently failed.
      const params = { company_id: cid, limit: 500 };
      if (jobId) params.job_id = jobId;
      const response = await api.get(API_ENDPOINTS.interviewsList, { params });
      const rows = safeData(response);

      const mapped = rows.map(iv => ({
        id: String(iv.id),
        applicationId: iv.application_id,
        // JOINed real name from the candidate's user record — never "Unknown"
        applicantName: iv.candidate_name || 'Applicant',
        applicantEmail: iv.candidate_email || '',
        applicantPhone: iv.candidate_phone || '',
        applicantAvatar: iv.candidate_avatar || null,
        position: iv.job_title || jobTitle || '',
        jobId: iv.job_id,
        type: iv.interview_type || 'video',
        typeName: iv.interview_type || 'Video',
        // Split scheduled_date into UI's date + time fields
        date: iv.scheduled_date ? new Date(iv.scheduled_date).toISOString().slice(0, 10) : '',
        time: iv.scheduled_date ? new Date(iv.scheduled_date).toISOString().slice(11, 16) : '',
        scheduledAt: iv.scheduled_date,
        duration: iv.duration_minutes || 45,
        status: iv.status || 'scheduled',
        statusColor: STATUS_COLOR[iv.status] || '#4A90E2',
        interviewer: iv.interviewer_name || '',
        interviewers: Array.isArray(iv.interviewers) ? iv.interviewers : [],
        location: iv.location || (iv.meeting_link ? 'Video Call' : 'Online'),
        meetingLink: iv.meeting_link || '',
        notes: iv.interviewer_notes || iv.notes || '',
        rating: iv.rating || null,
        feedback: iv.feedback || '',
        candidateJoinedAt: iv.candidate_joined_at || null,
        candidateLeftAt:   iv.candidate_left_at || null,
        candidateFeedback: iv.candidate_feedback || null,
        recordingUrl: iv.recording_url || null,
        applicationStage: iv.application_stage,
        decision: iv.decision || null,
      }));
      setInterviews(mapped);
      await AsyncStorage.setItem('businessInterviews', JSON.stringify(mapped)).catch(() => {});
    } catch (error) {
      console.warn('loadInterviews:', error?.message);
      // Offline cache fallback — but never mock data
      try {
        const stored = await AsyncStorage.getItem('businessInterviews');
        if (stored) setInterviews(JSON.parse(stored));
        else setInterviews([]);
      } catch { setInterviews([]); }
    }
  };

  // Load applicants
  const loadApplicants = async () => {
    try {
      // Try API first
      const cid = companyId || await AsyncStorage.getItem('company_id') || '1';
      const response = await api.get(API_ENDPOINTS.applications, { params: { company_id: parseInt(cid), limit: 500 } });
      const apiData = safeData(response);
      if (apiData.length > 0) {
        const mapped = apiData.map(a => ({
          id: a.id, name: a.candidate_name || 'Unknown', email: a.candidate_email || '',
          position: a.current_title || a.job_title || '', status: a.status || 'active',
        }));
        setApplicants(mapped);
        await AsyncStorage.setItem('businessApplicants', JSON.stringify(mapped));
        return;
      }
      const stored = await AsyncStorage.getItem('businessApplicants');
      if (stored) {
        setApplicants(JSON.parse(stored));
      } else {
        const sampleData = generateApplicantsData();
        setApplicants(sampleData);
        await AsyncStorage.setItem('businessApplicants', JSON.stringify(sampleData));
      }
    } catch (error) {
      console.error('Error loading applicants:', error);
      setApplicants(generateApplicantsData());
    }
  };

  // Load interviewers
  const loadInterviewers = async () => {
    try {
      const stored = await AsyncStorage.getItem('businessInterviewers');
      if (stored) {
        setInterviewers(JSON.parse(stored));
      } else {
        const sampleData = generateInterviewersData();
        setInterviewers(sampleData);
        await AsyncStorage.setItem('businessInterviewers', JSON.stringify(sampleData));
      }
    } catch (error) {
      console.error('Error loading interviewers:', error);
      setInterviewers(generateInterviewersData());
    }
  };

  // Generate comprehensive interviews data
  const generateInterviewsData = () => {
    const statuses = ['Scheduled', 'Completed', 'Cancelled', 'In Progress', 'Pending Feedback'];
    const formats = ['Video', 'In-person', 'Phone'];
    const positions = [
      'Senior Software Engineer', 'UX Designer', 'Product Manager', 'Data Scientist',
      'Marketing Manager', 'DevOps Engineer', 'Business Analyst', 'Frontend Developer'
    ];

    return Array.from({ length: 30 }, (_, index) => {
      const date = new Date();
      date.setDate(date.getDate() + Math.floor(Math.random() * 30) - 15);
      const type = interviewTypes[Math.floor(Math.random() * interviewTypes.length)];
      const status = statuses[Math.floor(Math.random() * statuses.length)];
      
      return {
        id: `int${index + 1}`,
        applicantId: `cand${Math.floor(Math.random() * 20) + 1}`,
        applicantName: `Applicant ${Math.floor(Math.random() * 20) + 1}`,
        applicantEmail: `applicant${Math.floor(Math.random() * 20) + 1}@example.com`,
        position: positions[Math.floor(Math.random() * positions.length)],
        type: type.id,
        typeName: type.name,
        typeIcon: type.icon,
        typeColor: type.color,
        status: status,
        format: formats[Math.floor(Math.random() * formats.length)],
        date: date.toISOString().split('T')[0],
        time: ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00'][Math.floor(Math.random() * 6)],
        duration: [30, 45, 60, 90][Math.floor(Math.random() * 4)],
        location: 'Conference Room A',
        meetingLink: 'https://meet.example.com/interview-' + (index + 1),
        interviewers: [
          { id: 'int1', name: 'Sarah Johnson', title: 'Senior Manager' },
          { id: 'int2', name: 'Michael Chen', title: 'Technical Lead' }
        ].slice(0, Math.floor(Math.random() * 2) + 1),
        notes: Math.random() > 0.5 ? 'Strong technical background, good communication skills' : '',
        score: status === 'Completed' ? Math.floor(Math.random() * 40) + 60 : null,
        feedback: status === 'Completed' ? 'Excellent applicant with strong problem-solving skills' : null,
        round: Math.floor(Math.random() * 3) + 1,
        createdDate: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        updatedDate: new Date().toISOString().split('T')[0],
        priority: Math.random() > 0.7 ? 'High' : Math.random() > 0.4 ? 'Medium' : 'Low',
        tags: ['Technical', 'Leadership', 'Communication'].filter(() => Math.random() > 0.6),
      };
    });
  };

  // Generate applicants data
  const generateApplicantsData = () => {
    const names = [
      'Emma Thompson', 'James Wilson', 'Sarah Johnson', 'Michael Brown', 'Lisa Chen',
      'David Rodriguez', 'Anna Martinez', 'Chris Anderson', 'Jessica Taylor', 'Daniel White'
    ];

    return Array.from({ length: 20 }, (_, index) => ({
      id: `cand${index + 1}`,
      name: names[Math.floor(Math.random() * names.length)],
      email: `applicant${index + 1}@example.com`,
      phone: `+44 ${Math.floor(Math.random() * 9000000000) + 1000000000}`,
      position: 'Software Engineer',
      experience: Math.floor(Math.random() * 10) + 1,
      skills: ['JavaScript', 'React', 'Node.js'].filter(() => Math.random() > 0.5),
      avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(names[Math.floor(Math.random() * names.length)])}&background=4A90E2&color=fff&size=200`,
    }));
  };

  // Generate interviewers data
  const generateInterviewersData = () => {
    const names = [
      'Sarah Johnson', 'Michael Chen', 'Emily Davis', 'Robert Wilson', 'Lisa Wang',
      'David Kim', 'Anna Rodriguez', 'Chris Thompson', 'Jessica Lee', 'Daniel Brown'
    ];

    const titles = [
      'Senior Manager', 'Technical Lead', 'HR Director', 'Team Lead', 'Principal Engineer',
      'Product Manager', 'Engineering Manager', 'Senior Developer', 'Architect', 'VP Engineering'
    ];

    return Array.from({ length: 10 }, (_, index) => ({
      id: `int${index + 1}`,
      name: names[Math.floor(Math.random() * names.length)],
      title: titles[Math.floor(Math.random() * titles.length)],
      email: `interviewer${index + 1}@company.com`,
      department: ['Engineering', 'Product', 'HR', 'Design'][Math.floor(Math.random() * 4)],
      expertise: ['Technical', 'Leadership', 'Cultural Fit'].filter(() => Math.random() > 0.5),
      availability: Math.random() > 0.3,
      rating: Math.floor(Math.random() * 2) + 4, // 4-5 stars
      avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(names[Math.floor(Math.random() * names.length)])}&background=10B981&color=fff&size=200`,
    }));
  };

  // Calculate statistics
  const calculateStatistics = () => {
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

    const stats = {
      total: interviews.length,
      scheduled: interviews.filter(i => i.status === 'Scheduled').length,
      completed: interviews.filter(i => i.status === 'Completed').length,
      cancelled: interviews.filter(i => i.status === 'Cancelled').length,
      today: interviews.filter(i => i.date === today).length,
      thisWeek: interviews.filter(i => {
        const interviewDate = new Date(i.date);
        return interviewDate >= now && interviewDate <= weekFromNow;
      }).length,
    };

    setStatistics(stats);
  };

  // ══════ SCHEDULE INTERVIEW ══════
  // Writes to application_interviews — the SAME row the candidate's
  // VideoInterviewScreen reads. The previous version referenced an undefined
  // `EP` and `apiRequest`, which threw a silent ReferenceError that the
  // outer try/catch swallowed — so interviews never actually persisted.
  const handleScheduleInterview = async () => {
    if (!schedForm.applicant_name || !schedForm.date || !schedForm.time) {
      Alert.alert('Error', 'Applicant name, date and time required');
      return;
    }
    // The form may carry an application_id from the picker. If not, the
    // interview can still be scheduled but won't surface to the candidate
    // until linked. Warn the recruiter in that case.
    const appId = schedForm.application_id || schedForm.applicant_id || null;
    if (!appId) {
      Alert.alert('No applicant selected', 'Pick an applicant before scheduling so they can see the interview.');
      return;
    }
    // Compose the scheduled_date column (DB stores a single timestamp).
    let scheduledAt;
    try {
      scheduledAt = new Date(`${String(schedForm.date).slice(0, 10)}T${String(schedForm.time).slice(0, 5)}:00`).toISOString();
    } catch { scheduledAt = new Date().toISOString(); }

    const payload = {
      application_id:   parseInt(appId, 10),
      interview_type:   (schedForm.type || 'technical').toLowerCase(),
      scheduled_date:   scheduledAt,
      duration_minutes: Number(schedForm.duration) || 60,
      meeting_link:     schedForm.meeting_link || null,
      location:         schedForm.location || 'Video Call',
      interviewer_name: schedForm.interviewers || '',
      instructions:     schedForm.preparation || '',
      notes:            schedForm.notes || null,
      status:           'scheduled',
      round_number:     1,
    };

    try {
      const r = await api.post(API_ENDPOINTS.interviews, payload);
      const created = r?.data?.data || r?.data || {};
      const newId = created.id || `int_${Date.now()}`;

      // Also bump the application stage so the pipeline reflects it
      try { await api.post(API_ENDPOINTS.scheduleStage(appId), { stage: 'interviewed' }); } catch (_) {}

      // Optimistic local merge (UI shape)
      setInterviews(prev => [{
        id:            String(newId),
        applicationId: payload.application_id,
        applicantName: schedForm.applicant_name,
        applicantEmail: schedForm.applicant_email || '',
        position:      schedForm.position || '',
        type:          payload.interview_type,
        typeName:      payload.interview_type,
        date:          String(schedForm.date).slice(0, 10),
        time:          String(schedForm.time).slice(0, 5),
        scheduledAt,
        duration:      payload.duration_minutes,
        status:        'scheduled',
        statusColor:   STATUS_COLOR.scheduled,
        interviewer:   payload.interviewer_name,
        location:      payload.location,
        meetingLink:   payload.meeting_link,
        notes:         payload.notes || '',
      }, ...prev]);

      setSchedForm({ applicant_name: '', applicant_email: '', application_id: '', position: '', type: 'Technical', date: '', time: '', duration: '60', location: 'Video Call', interviewers: '', notes: '', preparation: '', panel_size: '1' });
      setActiveTab('Scheduled');
      Alert.alert('Scheduled', `Interview scheduled for ${schedForm.applicant_name}`);
    } catch (e) {
      console.warn('handleScheduleInterview:', e?.message);
      Alert.alert('Could not schedule', e?.response?.data?.message || e?.message || 'Please try again.');
    }
  };

  // ══════ RESCHEDULE ══════
  // PUT to the application_interviews row so the candidate sees the new
  // date/time. Build a single scheduled_date timestamp from new_date+new_time.
  const handleRescheduleSubmit = async () => {
    if (!reschedForm.interview_id || !reschedForm.new_date || !reschedForm.new_time) {
      Alert.alert('Error', 'Select interview and new date/time');
      return;
    }
    let scheduledAt;
    try {
      scheduledAt = new Date(`${String(reschedForm.new_date).slice(0, 10)}T${String(reschedForm.new_time).slice(0, 5)}:00`).toISOString();
    } catch { scheduledAt = new Date().toISOString(); }
    try {
      await api.put(`${API_ENDPOINTS.interviews}/${reschedForm.interview_id}`, {
        scheduled_date: scheduledAt,
        status: 'rescheduled',
        updated_at: new Date().toISOString(),
      });
    } catch (e) { console.warn('reschedule API:', e?.message); }
    setInterviews(prev => prev.map(i =>
      String(i.id) === String(reschedForm.interview_id)
        ? { ...i, date: String(reschedForm.new_date).slice(0, 10), time: String(reschedForm.new_time).slice(0, 5), scheduledAt, status: 'rescheduled', statusColor: STATUS_COLOR.rescheduled }
        : i
    ));
    setReschedForm({ interview_id: '', new_date: '', new_time: '', reason: '', notify_applicant: true, notify_interviewers: true });
    setActiveTab('Scheduled');
    Alert.alert('Rescheduled', 'Interview rescheduled');
  };

  // ══════ FINAL INTERVIEW ══════
  const handleScheduleFinal = async () => {
    if (!finalForm.applicant_name || !finalForm.date) { Alert.alert('Error', 'Applicant and date required'); return; }
    const appId = finalForm.application_id || finalForm.applicant_id || null;
    if (!appId) { Alert.alert('No applicant', 'Pick an applicant first.'); return; }
    let scheduledAt;
    try {
      scheduledAt = new Date(`${String(finalForm.date).slice(0, 10)}T${String(finalForm.time || '10:00').slice(0, 5)}:00`).toISOString();
    } catch { scheduledAt = new Date().toISOString(); }
    const payload = {
      application_id:   parseInt(appId, 10),
      interview_type:   'final',
      scheduled_date:   scheduledAt,
      duration_minutes: Number(finalForm.duration) || 90,
      location:         finalForm.location || 'Office',
      interviewer_name: finalForm.panel || '',
      instructions:     finalForm.presentation_topic || finalForm.assessment_criteria || '',
      status:           'scheduled',
      round_number:     2,
    };
    try { await api.post(API_ENDPOINTS.interviews, payload); }
    catch (e) { console.warn('final interview API:', e?.message); }
    setInterviews(prev => [{
      id: `fin_${Date.now()}`,
      applicationId: payload.application_id,
      applicantName: finalForm.applicant_name,
      position: finalForm.position || '',
      type: 'final',
      typeName: 'Final Interview',
      date: String(finalForm.date).slice(0, 10),
      time: String(finalForm.time || '10:00').slice(0, 5),
      scheduledAt,
      duration: payload.duration_minutes,
      status: 'scheduled',
      statusColor: '#8B5CF6',
      location: payload.location,
      interviewer: payload.interviewer_name,
    }, ...prev]);
    setFinalForm({ applicant_name: '', application_id: '', position: '', date: '', time: '', duration: '90', location: 'Office', panel: '', presentation_topic: '', assessment_criteria: '', dress_code: 'Business Professional', offer_ready: false });
    setActiveTab('Scheduled');
    Alert.alert('Scheduled', 'Final interview scheduled');
  };

  // ══════ FIRST DAY ══════
  // This isn't an interview — it's an onboarding event. Kept local-only for
  // now because there's no canonical "onboarding_events" table; if/when one
  // exists, point this at it.
  const handleScheduleFirstDay = async () => {
    if (!firstDayForm.employee_name || !firstDayForm.start_date) {
      Alert.alert('Error', 'Employee name and date required');
      return;
    }
    const local = {
      ...firstDayForm,
      equipment: selectedEquipment.join(', '),
      type: 'First Day',
      status: 'Scheduled',
      company_id: parseInt(companyId) || 0,
      created_at: new Date().toISOString(),
      id: `fd_${Date.now()}`,
    };
    try {
      const cache = await AsyncStorage.getItem('onboarding_events');
      const arr = cache ? JSON.parse(cache) : [];
      arr.unshift(local);
      await AsyncStorage.setItem('onboarding_events', JSON.stringify(arr));
    } catch (_) {}
    setFirstDayForm({ employee_name: '', start_date: '', start_time: '09:00', location: 'Office', manager: '', buddy: '', equipment: '', welcome_message: '', agenda: '', special_requirements: '' });
    setSelectedEquipment([]);
    Alert.alert('Scheduled', `First day scheduled for ${local.employee_name}`);
  };

  // ══════ SAVE SETTINGS ══════
  const handleSaveSettings = async () => {
    try { await AsyncStorage.setItem('interview_settings', JSON.stringify(settingsForm)); Alert.alert('Saved', 'Settings saved'); } catch { Alert.alert('Error', 'Failed to save'); }
  };

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await loadAllData();
    setIsRefreshing(false);
  }, []);

  // Filter and sort interviews
  const getFilteredInterviews = () => {
    let filtered = [...interviews];

    // Optional applicant scope from route.params — only show interviews
    // for this candidate when the screen was opened from their detail screen
    // or after they were scheduled / interviewed.
    if (focusApplicantId) {
      filtered = filtered.filter(iv =>
        String(iv.applicationId) === String(focusApplicantId) ||
        String(iv.application_id) === String(focusApplicantId) ||
        String(iv.applicantId)   === String(focusApplicantId)
      );
    }

    // Filter by tab
    if (activeTab !== 'All') {
      filtered = filtered.filter(interview => interview.status === activeTab);
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(interview =>
        String(interview.applicantName ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(interview.position ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(interview.typeName ?? '').toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Filter by type
    if (filterType !== 'All') {
      filtered = filtered.filter(interview => interview.type === filterType);
    }

    // Sort interviews
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date':
          return new Date(b.date + ' ' + b.time) - new Date(a.date + ' ' + a.time);
        case 'applicant':
          return a.applicantName.localeCompare(b.applicantName);
        case 'type':
          return a.typeName.localeCompare(b.typeName);
        case 'score':
          return (b.score || 0) - (a.score || 0);
        default:
          return 0;
      }
    });

    return filtered;
  };

  // Handle interview actions
  const handleStartInterview = (interview) => {
    if (interview.format === 'Video') {
      if (navigation) {
        navigation.navigate('BusinessVideoInterviewScreen', {
          interviewId: interview.id,
          applicantId: interview.applicantId,
          interviewData: interview,
          jobId, jobTitle, companyLogo, companyName,
        });
      } else {
        Alert.alert('Success', 'Video interview started!');
      }
    } else {
      Alert.alert('Interview Started', `${interview.format} interview with ${interview.applicantName} has started`, [
        { text: 'Complete Now', onPress: () => handleCompleteInterview(interview) },
        { text: 'Add Feedback', onPress: () => handleAddFeedback(interview) },
        { text: 'OK' },
      ]);
    }
  };

  const handleRescheduleInterview = (interview) => {
    setSelectedInterview(interview);
    setReschedForm(p => ({ ...p, interview_id: interview.id || interview.serverId }));
    setActiveTab('Reschedule');
  };

  const handleCancelInterview = (interview) => {
    Alert.alert(
      'Cancel Interview',
      `Are you sure you want to cancel the interview with ${interview.applicantName}?`,
      [
        { text: 'No', style: 'cancel' },
        {
          text: 'Yes, Cancel',
          style: 'destructive',
          onPress: async () => {
            try { await api.put(API_ENDPOINTS.interviews + '/' + interview.id, { status: 'cancelled' }); } catch (e) { console.warn('API cancel failed:', e.message); }
            const updatedInterviews = interviews.map(i =>
              i.id === interview.id ? { ...i, status: 'Cancelled' } : i
            );
            setInterviews(updatedInterviews);
            await AsyncStorage.setItem('businessInterviews', JSON.stringify(updatedInterviews));
            Alert.alert('Success', 'Interview cancelled successfully!');
          },
        },
      ]
    );
  };

  const handleAddFeedback = (interview) => {
    setSelectedInterview(interview);
    setShowFeedbackModal(true);
  };

  const handleScheduleNew = () => {
    setActiveTab('Schedule');
  };

  // Delete interview
  const handleDeleteInterview = (interview) => {
    Alert.alert('Delete Interview', `Remove interview with ${interview.applicantName}?`, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: async () => {
        try { await api.delete(API_ENDPOINTS.interviews + '/' + interview.id); } catch (e) { console.warn('API delete failed:', e.message); }
        const updated = interviews.filter(i => i.id !== interview.id);
        setInterviews(updated);
        await AsyncStorage.setItem('businessInterviews', JSON.stringify(updated));
        Alert.alert('Deleted', 'Interview removed');
      }},
    ]);
  };

  // Mark as completed
  const handleCompleteInterview = async (interview) => {
    try { await api.put(API_ENDPOINTS.interviews + '/' + interview.id, { status: 'completed' }); } catch (e) { console.warn('API complete failed:', e.message); }
    const updated = interviews.map(i => i.id === interview.id ? { ...i, status: 'Completed' } : i);
    setInterviews(updated);
    await AsyncStorage.setItem('businessInterviews', JSON.stringify(updated));
    Alert.alert('Completed', `Interview with ${interview.applicantName} marked as completed`);
  };

  // Send reminder
  const handleSendReminder = (interview) => {
    Alert.alert('Reminder Sent', `Reminder sent to ${interview.applicantName} for ${interview.type} interview on ${new Date(interview.date).toLocaleDateString('en-GB')}`);
  };

  // Quick call/email
  const handleContactApplicant = (interview) => {
    Alert.alert(interview.applicantName, `Position: ${interview.position}`, [
      { text: 'Email', onPress: () => { if (interview.applicantEmail) { const { Linking } = require('react-native'); Linking.openURL('mailto:' + interview.applicantEmail); } else Alert.alert('No Email', 'No email available'); } },
      { text: 'Call', onPress: () => { if (interview.applicantPhone) { const { Linking } = require('react-native'); Linking.openURL('tel:' + interview.applicantPhone); } else Alert.alert('No Phone', 'No phone available'); } },
      { text: 'Close', style: 'cancel' },
    ]);
  };

  // Move applicant to next stage
  const handleMoveToNextStage = async (interview) => {
    Alert.alert('Move to Next Stage', `Advance ${interview.applicantName}?`, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Send Offer', onPress: async () => {
        try { await api.post(API_ENDPOINTS.offers, { application_id: interview.applicantId, candidate_name: interview.applicantName, company_id: parseInt(companyId) || 0, status: 'pending', job_id: interview.jobId || jobId }); } catch (e) { console.warn('Offer failed:', e.message); }
        Alert.alert('Offer Sent', `Offer created for ${interview.applicantName}`);
      }},
      { text: 'Reject', style: 'destructive', onPress: async () => {
        try { await api.put(API_ENDPOINTS.applications + '/' + interview.applicantId, { status: 'rejected' }); } catch {}
        Alert.alert('Rejected', `${interview.applicantName} has been rejected`);
      }},
    ]);
  };

  // Save feedback
  const saveFeedback = async (feedback, score) => {
    if (!selectedInterview) return;
    try { await api.put(API_ENDPOINTS.interviews + '/' + selectedInterview.id, { feedback, rating: parseInt(score), status: 'completed' }); } catch (e) { console.warn('API feedback save failed:', e.message); }
    const updatedInterviews = interviews.map(i =>
      i.id === selectedInterview.id
        ? { ...i, feedback, score: parseInt(score), status: 'Completed' }
        : i
    );
    setInterviews(updatedInterviews);
    await AsyncStorage.setItem('businessInterviews', JSON.stringify(updatedInterviews));
    setShowFeedbackModal(false);
    setSelectedInterview(null);
    Alert.alert('Success', 'Feedback saved successfully!');
  };

  // Export interviews
  const exportInterviews = async () => {
    try {
      const csvContent = generateCSV(getFilteredInterviews());
      await Share.share({
        message: csvContent,
        title: 'Interview Data Export',
      });
    } catch (error) {
      Alert.alert('Error', 'Failed to export interviews');
    }
  };

  // Generate CSV
  const generateCSV = (data) => {
    const headers = [
      'Applicant', 'Position', 'Type', 'Status', 'Date', 'Time',
      'Duration', 'Format', 'Score', 'Feedback'
    ];

    const rows = data.map(interview => [
      interview.applicantName,
      interview.position,
      interview.typeName,
      interview.status,
      interview.date,
      interview.time,
      `${interview.duration} min`,
      interview.format,
      interview.score || 'N/A',
      interview.feedback || 'N/A'
    ]);

    return [headers, ...rows].map(row => row.join(',')).join('\n');
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Scheduled': return '#3B82F6';
      case 'Completed': return '#10B981';
      case 'Cancelled': return '#EF4444';
      case 'In Progress': return '#F59E0B';
      case 'Pending Feedback': return '#8B5CF6';
      default: return '#64748B';
    }
  };

  // Render interview card
  const renderInterviewCard = ({ item }) => (
    <TouchableOpacity
      style={styles.interviewCard}
      onPress={() => {
        setSelectedInterview(item);
        setShowInterviewModal(true);
      }}
    >
      <View style={styles.cardHeader}>
        <View style={styles.interviewTypeIcon}>
          <MaterialCommunityIcons
            name={item.typeIcon}
            size={24}
            color={item.typeColor}
          />
        </View>
        <View style={styles.interviewInfo}>
          <Text style={styles.applicantName}>{item.applicantName}</Text>
          <Text style={styles.position}>{item.position}</Text>
          <Text style={styles.interviewType}>{item.typeName} Interview</Text>
        </View>
        <View style={styles.statusContainer}>
          <View style={[styles.statusBadge, { backgroundColor: getStatusColor(item.status) }]}>
            <Text style={styles.statusText}>{item.status}</Text>
          </View>
        </View>
      </View>

      <View style={styles.cardDetails}>
        <View style={styles.detailRow}>
          <MaterialCommunityIcons name="calendar" size={16} color="#64748B" />
          <Text style={styles.detailText}>{item.date} at {item.time}</Text>
        </View>
        <View style={styles.detailRow}>
          <MaterialCommunityIcons name="clock-outline" size={16} color="#64748B" />
          <Text style={styles.detailText}>{item.duration} minutes</Text>
        </View>
        <View style={styles.detailRow}>
          <MaterialCommunityIcons
            name={item.format === 'Video' ? 'video' : item.format === 'Phone' ? 'phone' : 'office-building'}
            size={16}
            color="#64748B"
          />
          <Text style={styles.detailText}>{item.format}</Text>
        </View>
        <View style={styles.detailRow}>
          <MaterialCommunityIcons name="account-group" size={16} color="#64748B" />
          <Text style={styles.detailText}>{(item.interviewers || []).length} interviewer{(item.interviewers || []).length > 1 ? 's' : ''}</Text>
        </View>
      </View>

      {item.score && (
        <View style={styles.scoreContainer}>
          <Text style={styles.scoreLabel}>Score:</Text>
          <Text style={[styles.scoreValue, { color: item.score >= 80 ? '#10B981' : item.score >= 60 ? '#F59E0B' : '#EF4444' }]}>
            {item.score}%
          </Text>
        </View>
      )}

      <View style={styles.cardActions}>
        {item.status === 'Scheduled' && (
          <>
            <TouchableOpacity
              style={styles.actionButton}
              onPress={() => handleStartInterview(item)}
            >
              <MaterialCommunityIcons name="play" size={16} color="#4A90E2" />
              <Text style={styles.actionButtonText}>Start</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.actionButton}
              onPress={() => handleRescheduleInterview(item)}
            >
              <MaterialCommunityIcons name="calendar-edit" size={16} color="#4A90E2" />
              <Text style={styles.actionButtonText}>Reschedule</Text>
            </TouchableOpacity>
          </>
        )}
        {item.status === 'Completed' && !item.feedback && (
          <TouchableOpacity
            style={[styles.actionButton, styles.primaryActionButton]}
            onPress={() => handleAddFeedback(item)}
          >
            <MaterialCommunityIcons name="comment-plus" size={16} color="#ffffff" />
            <Text style={[styles.actionButtonText, { color: '#ffffff' }]}>Add Feedback</Text>
          </TouchableOpacity>
        )}
        {item.status === 'Scheduled' && (
          <TouchableOpacity
            style={[styles.actionButton, styles.dangerActionButton]}
            onPress={() => handleCancelInterview(item)}
          >
            <MaterialCommunityIcons name="cancel" size={16} color="#ffffff" />
            <Text style={[styles.actionButtonText, { color: '#ffffff' }]}>Cancel</Text>
          </TouchableOpacity>
        )}
      </View>
    </TouchableOpacity>
  );

  // Render statistics
  const renderStatistics = () => (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.statsContainer}>
      <View style={styles.statCard}>
        <Text style={styles.statValue}>{statistics.total}</Text>
        <Text style={styles.statLabel}>Total</Text>
      </View>
      <View style={styles.statCard}>
        <Text style={[styles.statValue, { color: '#3B82F6' }]}>{statistics.scheduled}</Text>
        <Text style={styles.statLabel}>Scheduled</Text>
      </View>
      <View style={styles.statCard}>
        <Text style={[styles.statValue, { color: '#10B981' }]}>{statistics.completed}</Text>
        <Text style={styles.statLabel}>Completed</Text>
      </View>
      <View style={styles.statCard}>
        <Text style={[styles.statValue, { color: '#EF4444' }]}>{statistics.cancelled}</Text>
        <Text style={styles.statLabel}>Cancelled</Text>
      </View>
      <View style={styles.statCard}>
        <Text style={[styles.statValue, { color: '#F59E0B' }]}>{statistics.today}</Text>
        <Text style={styles.statLabel}>Today</Text>
      </View>
      <View style={styles.statCard}>
        <Text style={[styles.statValue, { color: '#8B5CF6' }]}>{statistics.thisWeek}</Text>
        <Text style={styles.statLabel}>This Week</Text>
      </View>
    </ScrollView>
  );

  // Render tabs
  const renderTabs = () => (
    <View>
      {/* Status Filter Tabs */}
      <View style={styles.tabsContainer}>
        {['All', 'Scheduled', 'Completed', 'Cancelled'].map((tab) => (
          <TouchableOpacity
            key={tab}
            style={[styles.tab, activeTab === tab && styles.activeTab]}
            onPress={() => setActiveTab(tab)}
          >
            <Text style={[styles.tabText, activeTab === tab && styles.activeTabText]}>
              {tab}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
      {/* Action Tabs */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ backgroundColor: '#FFF', borderBottomWidth: 1, borderBottomColor: '#E2E8F0', paddingVertical: 3, flexGrow: 0, flexShrink: 0, maxHeight: 34 }} contentContainerStyle={{ paddingHorizontal: 10, gap: 5, alignItems: 'center' }}>
        {[
          { k: 'Schedule', i: 'calendar-plus', c: '#4A90E2' },
          { k: 'Reschedule', i: 'calendar-sync', c: '#F59E0B' },
          { k: 'Final', i: 'star-circle', c: '#8B5CF6' },
          { k: 'First Day', i: 'account-star', c: '#10B981' },
          { k: 'Settings', i: 'cog', c: '#6B7280' },
        ].map((tab) => (
          <TouchableOpacity
            key={tab.k}
            style={{ flexDirection: 'row', alignItems: 'center', paddingHorizontal: 10, paddingVertical: 5, borderRadius: 16, backgroundColor: activeTab === tab.k ? tab.c + '15' : '#F8FAFC', borderWidth: 1, borderColor: activeTab === tab.k ? tab.c : '#E2E8F0' }}
            onPress={() => setActiveTab(tab.k)}
          >
            <MaterialCommunityIcons name={tab.i} size={12} color={activeTab === tab.k ? tab.c : '#94A3B8'} />
            <Text style={{ marginLeft: 4, fontSize: 11, fontWeight: '600', color: activeTab === tab.k ? tab.c : '#94A3B8' }}>
              {tab.k}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size={36} color="#4A90E2" />
        <Text style={styles.loadingText}>Loading interviews...</Text>
      </View>
    );
  }

  const filteredInterviews = getFilteredInterviews();

  // ══════ SCHEDULE TAB ══════
  const renderScheduleTab = () => (
    <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : 'height'} style={{ flex: 1 }}>
      <ScrollView style={{ padding: 16 }} refreshControl={<RefreshControl refreshing={isRefreshing} onRefresh={handleRefresh} />}>
        <Text style={{ fontSize: 18, fontWeight: 'bold', color: '#1F2937', marginBottom: 16 }}>Schedule New Interview</Text>
        {[['Applicant Name *', 'applicant_name', 'Full name'], ['Email', 'applicant_email', 'email@example.com'], ['Position', 'position', 'Job title'], ['Date *', 'date', 'YYYY-MM-DD'], ['Time *', 'time', 'HH:MM'], ['Interviewers', 'interviewers', 'Names, comma separated'], ['Notes', 'notes', 'Interview notes...'], ['Preparation', 'preparation', 'What to prepare...']].map(([l, k, ph]) => (
          <View key={k} style={{ marginBottom: 12 }}><Text style={{ fontSize: 13, fontWeight: '600', color: '#374151', marginBottom: 4 }}>{l}</Text>
            <TextInput style={{ backgroundColor: '#F3F4F6', borderRadius: 8, padding: 11, fontSize: 14, color: '#1F2937', borderWidth: 1, borderColor: '#E5E7EB', ...(k === 'notes' || k === 'preparation' ? { height: 70, textAlignVertical: 'top' } : {}) }}
              placeholder={ph} placeholderTextColor="#9CA3AF" value={schedForm[k]}
              onChangeText={v => setSchedForm(p => ({ ...p, [k]: v }))} multiline={k === 'notes' || k === 'preparation'} /></View>))}
        <Text style={{ fontSize: 13, fontWeight: '600', color: '#374151', marginBottom: 6 }}>Interview Type</Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 12 }}>
          {INTERVIEW_TYPES.map(t => (<TouchableOpacity key={t} style={{ paddingHorizontal: 14, paddingVertical: 7, borderRadius: 16, marginRight: 8, backgroundColor: schedForm.type === t ? '#4A90E2' : '#F1F5F9' }}
            onPress={() => setSchedForm(p => ({ ...p, type: t }))}><Text style={{ color: schedForm.type === t ? '#FFF' : '#6B7280', fontWeight: '600', fontSize: 12 }}>{t}</Text></TouchableOpacity>))}</ScrollView>
        <Text style={{ fontSize: 13, fontWeight: '600', color: '#374151', marginBottom: 6 }}>Duration</Text>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
          {DURATIONS.map(d => (<TouchableOpacity key={d} style={{ paddingHorizontal: 14, paddingVertical: 7, borderRadius: 16, backgroundColor: schedForm.duration === d ? '#4A90E2' : '#F1F5F9' }}
            onPress={() => setSchedForm(p => ({ ...p, duration: d }))}><Text style={{ color: schedForm.duration === d ? '#FFF' : '#6B7280', fontWeight: '600', fontSize: 12 }}>{d} min</Text></TouchableOpacity>))}</View>
        <Text style={{ fontSize: 13, fontWeight: '600', color: '#374151', marginBottom: 6 }}>Location</Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 12 }}>
          {LOCATIONS.map(l => (<TouchableOpacity key={l} style={{ paddingHorizontal: 14, paddingVertical: 7, borderRadius: 16, marginRight: 8, backgroundColor: schedForm.location === l ? '#4A90E2' : '#F1F5F9' }}
            onPress={() => setSchedForm(p => ({ ...p, location: l }))}><Text style={{ color: schedForm.location === l ? '#FFF' : '#6B7280', fontWeight: '600', fontSize: 12 }}>{l}</Text></TouchableOpacity>))}</ScrollView>
        <TouchableOpacity style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: '#4A90E2', borderRadius: 10, padding: 14, marginTop: 8 }} onPress={handleScheduleInterview}>
          <MaterialCommunityIcons name="calendar-check" size={20} color="#FFF" /><Text style={{ color: '#FFF', fontWeight: '700', fontSize: 16, marginLeft: 8 }}>Schedule Interview</Text></TouchableOpacity>
        <View style={{ height: 40 }} /></ScrollView></KeyboardAvoidingView>);

  // ══════ RESCHEDULE TAB ══════
  const renderRescheduleTab = () => (
    <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : 'height'} style={{ flex: 1 }}>
      <ScrollView style={{ padding: 16 }}>
        <Text style={{ fontSize: 18, fontWeight: 'bold', color: '#1F2937', marginBottom: 16 }}>Reschedule Interview</Text>
        <Text style={{ fontSize: 13, fontWeight: '600', color: '#374151', marginBottom: 8 }}>Select Interview</Text>
        {interviews.filter(i => (i.status || '').toLowerCase() === 'scheduled').slice(0, 10).map((int, i) => (
          <TouchableOpacity key={i} style={{ padding: 12, borderRadius: 8, borderWidth: 2, borderColor: reschedForm.interview_id === (int.id || int.serverId) ? '#4A90E2' : '#E5E7EB', backgroundColor: reschedForm.interview_id === (int.id || int.serverId) ? '#4A90E215' : '#FFF', marginBottom: 6 }}
            onPress={() => setReschedForm(p => ({ ...p, interview_id: int.id || int.serverId }))}>
            <Text style={{ fontWeight: '600', color: '#1F2937' }}>{int.applicantName || int.applicant_name || 'Unknown'}</Text>
            <Text style={{ fontSize: 12, color: '#6B7280' }}>{int.typeName || int.type || ''} \u00B7 {int.date || ''} at {int.time || ''}</Text></TouchableOpacity>))}
        {[['New Date *', 'new_date', 'YYYY-MM-DD'], ['New Time *', 'new_time', 'HH:MM'], ['Reason', 'reason', 'Reason for rescheduling...']].map(([l, k, ph]) => (
          <View key={k} style={{ marginBottom: 12 }}><Text style={{ fontSize: 13, fontWeight: '600', color: '#374151', marginBottom: 4 }}>{l}</Text>
            <TextInput style={{ backgroundColor: '#F3F4F6', borderRadius: 8, padding: 11, fontSize: 14, color: '#1F2937', borderWidth: 1, borderColor: '#E5E7EB', ...(k === 'reason' ? { height: 70, textAlignVertical: 'top' } : {}) }}
              placeholder={ph} placeholderTextColor="#9CA3AF" value={reschedForm[k]}
              onChangeText={v => setReschedForm(p => ({ ...p, [k]: v }))} multiline={k === 'reason'} /></View>))}
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 12 }}>
          <Text style={{ fontWeight: '600', color: '#374151' }}>Notify Applicant</Text>
          <Switch value={reschedForm.notify_applicant} onValueChange={v => setReschedForm(p => ({ ...p, notify_applicant: v }))} trackColor={{ true: '#4A90E2' }} /></View>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 12 }}>
          <Text style={{ fontWeight: '600', color: '#374151' }}>Notify Interviewers</Text>
          <Switch value={reschedForm.notify_interviewers} onValueChange={v => setReschedForm(p => ({ ...p, notify_interviewers: v }))} trackColor={{ true: '#4A90E2' }} /></View>
        <TouchableOpacity style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F59E0B', borderRadius: 10, padding: 14, marginTop: 8 }} onPress={handleRescheduleSubmit}>
          <MaterialCommunityIcons name="calendar-sync" size={20} color="#FFF" /><Text style={{ color: '#FFF', fontWeight: '700', fontSize: 16, marginLeft: 8 }}>Reschedule</Text></TouchableOpacity>
        <View style={{ height: 40 }} /></ScrollView></KeyboardAvoidingView>);

  // ══════ FINAL INTERVIEW TAB ══════
  const renderFinalTab = () => (
    <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : 'height'} style={{ flex: 1 }}>
      <ScrollView style={{ padding: 16 }}>
        <Text style={{ fontSize: 18, fontWeight: 'bold', color: '#1F2937', marginBottom: 16 }}>Schedule Final Interview</Text>
        {[['Applicant Name *', 'applicant_name', 'Full name'], ['Position', 'position', 'Job title'], ['Date *', 'date', 'YYYY-MM-DD'], ['Time', 'time', 'HH:MM'], ['Panel Members', 'panel', 'Names, comma separated'], ['Presentation Topic', 'presentation_topic', 'Topic for presentation...'], ['Assessment Criteria', 'assessment_criteria', 'Key evaluation points...']].map(([l, k, ph]) => (
          <View key={k} style={{ marginBottom: 12 }}><Text style={{ fontSize: 13, fontWeight: '600', color: '#374151', marginBottom: 4 }}>{l}</Text>
            <TextInput style={{ backgroundColor: '#F3F4F6', borderRadius: 8, padding: 11, fontSize: 14, color: '#1F2937', borderWidth: 1, borderColor: '#E5E7EB', ...(k === 'presentation_topic' || k === 'assessment_criteria' ? { height: 70, textAlignVertical: 'top' } : {}) }}
              placeholder={ph} placeholderTextColor="#9CA3AF" value={finalForm[k]}
              onChangeText={v => setFinalForm(p => ({ ...p, [k]: v }))} multiline={k === 'presentation_topic' || k === 'assessment_criteria'} /></View>))}
        <Text style={{ fontSize: 13, fontWeight: '600', color: '#374151', marginBottom: 6 }}>Duration</Text>
        <View style={{ flexDirection: 'row', gap: 8, marginBottom: 12 }}>{['60', '90', '120', '180'].map(d => (
          <TouchableOpacity key={d} style={{ flex: 1, padding: 8, borderRadius: 8, backgroundColor: finalForm.duration === d ? '#8B5CF6' : '#F1F5F9', alignItems: 'center' }}
            onPress={() => setFinalForm(p => ({ ...p, duration: d }))}><Text style={{ color: finalForm.duration === d ? '#FFF' : '#6B7280', fontWeight: '600', fontSize: 12 }}>{d}m</Text></TouchableOpacity>))}</View>
        <Text style={{ fontSize: 13, fontWeight: '600', color: '#374151', marginBottom: 6 }}>Dress Code</Text>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>{['Business Professional', 'Business Casual', 'Smart Casual', 'Casual'].map(d => (
          <TouchableOpacity key={d} style={{ paddingHorizontal: 14, paddingVertical: 7, borderRadius: 16, backgroundColor: finalForm.dress_code === d ? '#8B5CF6' : '#F1F5F9' }}
            onPress={() => setFinalForm(p => ({ ...p, dress_code: d }))}><Text style={{ color: finalForm.dress_code === d ? '#FFF' : '#6B7280', fontWeight: '600', fontSize: 12 }}>{d}</Text></TouchableOpacity>))}</View>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 12, borderTopWidth: 1, borderTopColor: '#F3F4F6' }}>
          <Text style={{ fontWeight: '600', color: '#374151' }}>Offer Ready</Text>
          <Switch value={finalForm.offer_ready} onValueChange={v => setFinalForm(p => ({ ...p, offer_ready: v }))} trackColor={{ true: '#8B5CF6' }} /></View>
        <TouchableOpacity style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: '#8B5CF6', borderRadius: 10, padding: 14, marginTop: 8 }} onPress={handleScheduleFinal}>
          <MaterialCommunityIcons name="star-circle" size={20} color="#FFF" /><Text style={{ color: '#FFF', fontWeight: '700', fontSize: 16, marginLeft: 8 }}>Schedule Final Interview</Text></TouchableOpacity>
        <View style={{ height: 40 }} /></ScrollView></KeyboardAvoidingView>);

  // ══════ FIRST DAY TAB ══════
  const renderFirstDayTab = () => (
    <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : 'height'} style={{ flex: 1 }}>
      <ScrollView style={{ padding: 16 }}>
        <Text style={{ fontSize: 18, fontWeight: 'bold', color: '#1F2937', marginBottom: 16 }}>Schedule First Day</Text>
        {[['New Employee Name *', 'employee_name', 'Full name'], ['Start Date *', 'start_date', 'YYYY-MM-DD'], ['Start Time', 'start_time', '09:00'], ['Location', 'location', 'Office / Remote'], ['Manager', 'manager', 'Direct manager name'], ['Buddy', 'buddy', 'Onboarding buddy name'], ['Welcome Message', 'welcome_message', 'Personal welcome note...'], ['Agenda', 'agenda', 'First day agenda items...'], ['Special Requirements', 'special_requirements', 'Dietary, accessibility, etc.']].map(([l, k, ph]) => (
          <View key={k} style={{ marginBottom: 12 }}><Text style={{ fontSize: 13, fontWeight: '600', color: '#374151', marginBottom: 4 }}>{l}</Text>
            <TextInput style={{ backgroundColor: '#F3F4F6', borderRadius: 8, padding: 11, fontSize: 14, color: '#1F2937', borderWidth: 1, borderColor: '#E5E7EB', ...(k === 'welcome_message' || k === 'agenda' || k === 'special_requirements' ? { height: 70, textAlignVertical: 'top' } : {}) }}
              placeholder={ph} placeholderTextColor="#9CA3AF" value={firstDayForm[k]}
              onChangeText={v => setFirstDayForm(p => ({ ...p, [k]: v }))} multiline={k === 'welcome_message' || k === 'agenda' || k === 'special_requirements'} /></View>))}
        <Text style={{ fontSize: 13, fontWeight: '600', color: '#374151', marginBottom: 8 }}>Equipment Checklist</Text>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>{EQUIPMENT_OPTIONS.map(eq => (
          <TouchableOpacity key={eq} style={{ paddingHorizontal: 12, paddingVertical: 6, borderRadius: 14, backgroundColor: selectedEquipment.includes(eq) ? '#10B981' : '#F1F5F9' }}
            onPress={() => setSelectedEquipment(prev => prev.includes(eq) ? prev.filter(e => e !== eq) : [...prev, eq])}>
            <Text style={{ color: selectedEquipment.includes(eq) ? '#FFF' : '#6B7280', fontWeight: '600', fontSize: 12 }}>{selectedEquipment.includes(eq) ? '\u2713 ' : ''}{eq}</Text></TouchableOpacity>))}</View>
        <TouchableOpacity style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: '#10B981', borderRadius: 10, padding: 14, marginTop: 8 }} onPress={handleScheduleFirstDay}>
          <MaterialCommunityIcons name="account-star" size={20} color="#FFF" /><Text style={{ color: '#FFF', fontWeight: '700', fontSize: 16, marginLeft: 8 }}>Schedule First Day</Text></TouchableOpacity>
        <View style={{ height: 40 }} /></ScrollView></KeyboardAvoidingView>);

  // ══════ SETTINGS TAB ══════
  const renderSettingsTab = () => (
    <ScrollView style={{ flex: 1, padding: 16 }}>
      <Text style={{ fontSize: 18, fontWeight: 'bold', color: '#1F2937', marginBottom: 16 }}>Interview Settings</Text>
      {[['Working Hours Start', 'working_hours_start', '09:00'], ['Working Hours End', 'working_hours_end', '18:00'], ['Buffer Time (mins)', 'buffer_time', '15'], ['Max Interviews/Day', 'max_interviews_per_day', '8'], ['Reminder (hours before)', 'reminder_hours_before', '24'], ['Default Duration (mins)', 'default_duration', '60'], ['Default Location', 'default_location', 'Video Call'], ['Timezone', 'timezone', 'Europe/London']].map(([l, k, ph]) => (
        <View key={k} style={{ marginBottom: 12 }}><Text style={{ fontSize: 13, fontWeight: '600', color: '#374151', marginBottom: 4 }}>{l}</Text>
          <TextInput style={{ backgroundColor: '#F3F4F6', borderRadius: 8, padding: 11, fontSize: 14, color: '#1F2937', borderWidth: 1, borderColor: '#E5E7EB' }}
            placeholder={ph} placeholderTextColor="#9CA3AF" value={settingsForm[k]}
            onChangeText={v => setSettingsForm(p => ({ ...p, [k]: v }))} keyboardType={['buffer_time', 'max_interviews_per_day', 'reminder_hours_before', 'default_duration'].includes(k) ? 'numeric' : 'default'} /></View>))}
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 12, borderTopWidth: 1, borderTopColor: '#F3F4F6' }}>
        <Text style={{ fontWeight: '600', color: '#374151' }}>Auto Reminders</Text>
        <Switch value={settingsForm.auto_reminders} onValueChange={v => setSettingsForm(p => ({ ...p, auto_reminders: v }))} trackColor={{ true: '#4A90E2' }} /></View>
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 12, borderTopWidth: 1, borderTopColor: '#F3F4F6' }}>
        <Text style={{ fontWeight: '600', color: '#374151' }}>Allow Self-Scheduling</Text>
        <Switch value={settingsForm.allow_self_schedule} onValueChange={v => setSettingsForm(p => ({ ...p, allow_self_schedule: v }))} trackColor={{ true: '#4A90E2' }} /></View>
      <TouchableOpacity style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: '#4A90E2', borderRadius: 10, padding: 14, marginTop: 12 }} onPress={handleSaveSettings}>
        <MaterialCommunityIcons name="content-save" size={20} color="#FFF" /><Text style={{ color: '#FFF', fontWeight: '700', fontSize: 16, marginLeft: 8 }}>Save Settings</Text></TouchableOpacity>
      <View style={{ height: 40 }} /></ScrollView>);

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#4A90E2" />
      
      {/* Header */}
      <LinearGradient colors={['#4A90E2', '#357ABD']} style={styles.header}>
        <View style={styles.headerContent}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation?.goBack()}
          >
            <Ionicons name="arrow-back" size={24} color="white" />
          </TouchableOpacity>
          {companyLogo ? (
            <Image source={{ uri: companyLogo || undefined }} style={{ width: 30, height: 30, borderRadius: 15, marginRight: 8 }} />
          ) : (
            <MaterialCommunityIcons name="office-building" size={24} color="white" style={{ marginRight: 8 }} />
          )}
          <View style={{ flex: 1 }}>
            <Text style={styles.headerTitle}>Interview Management</Text>
            {jobTitle ? <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12 }}>{jobTitle}</Text> : null}
          </View>
          <TouchableOpacity style={styles.headerAction} onPress={exportInterviews}>
            <MaterialCommunityIcons name="export" size={24} color="white" />
          </TouchableOpacity>
        </View>
      </LinearGradient>

      {/* Search Bar */}
      <View style={styles.searchContainer}>
        <MaterialCommunityIcons name="magnify" size={20} color="#64748B" />
        <TextInput
          style={styles.searchInput}
          placeholder="Search interviews, applicants, positions..."
          value={searchQuery}
          onChangeText={setSearchQuery}
          placeholderTextColor="#94A3B8"
        />
        {searchQuery.length > 0 && (
          <TouchableOpacity onPress={() => setSearchQuery('')}>
            <MaterialCommunityIcons name="close-circle" size={20} color="#64748B" />
          </TouchableOpacity>
        )}
      </View>

      {/* Statistics */}
      {renderStatistics()}

      {/* Tabs */}
      {renderTabs()}

      {/* Content Area */}
      <View style={{ flex: 1 }}>
      {['Schedule', 'Reschedule', 'Final', 'First Day', 'Settings'].includes(activeTab) ? (
        activeTab === 'Schedule' ? renderScheduleTab() :
        activeTab === 'Reschedule' ? renderRescheduleTab() :
        activeTab === 'Final' ? renderFinalTab() :
        activeTab === 'First Day' ? renderFirstDayTab() :
        renderSettingsTab()
      ) : (
      <FlatList
        style={{ flex: 1 }}
        data={filteredInterviews}
        renderItem={renderInterviewCard}
        keyExtractor={(item) => item.id}
        contentContainerStyle={styles.listContainer}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            colors={['#4A90E2']}
            tintColor="#4A90E2"
          />
        }
        ListEmptyComponent={
          <View style={styles.emptyState}>
            <MaterialCommunityIcons name="calendar-clock" size={64} color="#CBD5E1" />
            <Text style={styles.emptyTitle}>No Interviews Found</Text>
            <Text style={styles.emptyDescription}>
              {searchQuery || activeTab !== 'All'
                ? 'Try adjusting your search or filters'
                : 'No interviews scheduled'}
            </Text>
          </View>
        }
      />
      )}
      </View>

      {/* Floating Action Button */}
      <TouchableOpacity style={styles.fab} onPress={handleScheduleNew}>
        <MaterialCommunityIcons name="calendar-plus" size={24} color="#ffffff" />
      </TouchableOpacity>

      {/* Feedback Modal */}
      <Modal
        visible={showFeedbackModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowFeedbackModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Add Interview Feedback</Text>
              <TouchableOpacity onPress={() => setShowFeedbackModal(false)}>
                <MaterialCommunityIcons name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.modalContent}>
              {selectedInterview && (
                <>
                  <Text style={styles.modalSubtitle}>
                    {selectedInterview.applicantName} - {selectedInterview.position}
                  </Text>
                  
                  <View style={styles.inputGroup}>
                    <Text style={styles.inputLabel}>Score (0-100)</Text>
                    <TextInput
                      style={styles.textInput}
                      placeholder="Enter score (0-100)"
                      keyboardType="numeric"
                      maxLength={3}
                      value={feedbackScore}
                      onChangeText={setFeedbackScore}
                    />
                  </View>

                  <View style={styles.inputGroup}>
                    <Text style={styles.inputLabel}>Feedback</Text>
                    <TextInput
                      style={[styles.textInput, styles.textArea]}
                      placeholder="Enter detailed feedback..."
                      multiline
                      numberOfLines={4}
                      textAlignVertical="top"
                      value={feedbackText}
                      onChangeText={setFeedbackText}
                    />
                  </View>

                  <View style={styles.modalActions}>
                    <TouchableOpacity
                      style={styles.cancelButton}
                      onPress={() => setShowFeedbackModal(false)}
                    >
                      <Text style={styles.cancelButtonText}>Cancel</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.saveButton}
                      onPress={() => { saveFeedback(feedbackText || 'No feedback provided', feedbackScore || '0'); setFeedbackText(''); setFeedbackScore(''); }}
                    >
                      <Text style={styles.saveButtonText}>Save Feedback</Text>
                    </TouchableOpacity>
                  </View>
                </>
              )}
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* Schedule New Interview Modal */}
      <Modal visible={showScheduleModal} transparent animationType="slide" onRequestClose={() => setShowScheduleModal(false)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Schedule Interview</Text>
              <TouchableOpacity onPress={() => setShowScheduleModal(false)}>
                <MaterialCommunityIcons name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.modalContent}>
              {[
                ['Applicant Name *', 'applicantName', 'John Smith'],
                ['Position', 'position', 'Software Engineer'],
                ['Date *', 'date', 'YYYY-MM-DD'],
                ['Time', 'time', '10:00 AM'],
                ['Duration (mins)', 'duration', '45'],
                ['Notes', 'notes', 'Additional notes...'],
              ].map(([label, key, ph]) => (
                <View key={key} style={styles.inputGroup}>
                  <Text style={styles.inputLabel}>{label}</Text>
                  <TextInput style={key === 'notes' ? [styles.textInput, styles.textArea] : styles.textInput}
                    placeholder={ph} placeholderTextColor="#9CA3AF"
                    value={scheduleForm[key]} onChangeText={v => setScheduleForm(p => ({ ...p, [key]: v }))}
                    multiline={key === 'notes'} numberOfLines={key === 'notes' ? 3 : 1} />
                </View>
              ))}
              <Text style={styles.inputLabel}>Interview Type</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 12 }}>
                {['Technical', 'Behavioral', 'Panel', 'Final'].map(t => (
                  <TouchableOpacity key={t}
                    style={{ paddingHorizontal: 16, paddingVertical: 8, borderRadius: 20, marginRight: 8, backgroundColor: scheduleForm.type === t.toLowerCase() ? '#4A90E2' : '#F1F5F9' }}
                    onPress={() => setScheduleForm(p => ({ ...p, type: t.toLowerCase() }))}>
                    <Text style={{ color: scheduleForm.type === t.toLowerCase() ? '#FFF' : '#64748B', fontWeight: '600' }}>{t}</Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
              <Text style={styles.inputLabel}>Format</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 16 }}>
                {['Video', 'In-Person', 'Phone'].map(f => (
                  <TouchableOpacity key={f}
                    style={{ paddingHorizontal: 16, paddingVertical: 8, borderRadius: 20, marginRight: 8, backgroundColor: scheduleForm.format === f ? '#4A90E2' : '#F1F5F9' }}
                    onPress={() => setScheduleForm(p => ({ ...p, format: f }))}>
                    <Text style={{ color: scheduleForm.format === f ? '#FFF' : '#64748B', fontWeight: '600' }}>{f}</Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
              <View style={styles.modalActions}>
                <TouchableOpacity style={styles.cancelButton} onPress={() => setShowScheduleModal(false)}>
                  <Text style={styles.cancelButtonText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.saveButton} onPress={async () => {
                  if (!scheduleForm.applicantName || !scheduleForm.date) { Alert.alert('Error', 'Name and date are required'); return; }
                  const newInterview = {
                    id: 'iv_' + Date.now(), applicantName: scheduleForm.applicantName, position: scheduleForm.position || jobTitle || '',
                    type: scheduleForm.type, date: scheduleForm.date, time: scheduleForm.time || '10:00',
                    duration: parseInt(scheduleForm.duration) || 45, status: 'Scheduled', format: scheduleForm.format,
                    notes: scheduleForm.notes, score: null, feedback: null, interviewers: [],
                  };
                  try { const res = await api.post(API_ENDPOINTS.interviews, {
                    candidate_name: newInterview.applicantName, interview_date: newInterview.date, interview_type: newInterview.type,
                    status: 'scheduled', duration: newInterview.duration, location: newInterview.format,
                    notes: newInterview.notes, company_id: parseInt(companyId) || 0, job_id: jobId,
                  }); if (res.data?.data?.id) newInterview.id = res.data.data.id;
                  } catch (e) { console.warn('API schedule failed:', e.message); }
                  setInterviews(prev => [newInterview, ...prev]);
                  await AsyncStorage.setItem('businessInterviews', JSON.stringify([newInterview, ...interviews]));
                  setShowScheduleModal(false);
                  setScheduleForm({ applicantName: '', position: '', type: 'technical', date: '', time: '', duration: '45', format: 'Video', notes: '' });
                  Alert.alert('Scheduled', `Interview with ${newInterview.applicantName} scheduled`);
                }}>
                  <Text style={styles.saveButtonText}>Schedule Interview</Text>
                </TouchableOpacity>
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* Reschedule Modal */}
      <Modal visible={showRescheduleModal} transparent animationType="slide" onRequestClose={() => setShowRescheduleModal(false)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Reschedule Interview</Text>
              <TouchableOpacity onPress={() => setShowRescheduleModal(false)}>
                <MaterialCommunityIcons name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>
            {selectedInterview && <ScrollView style={styles.modalContent}>
              <Text style={styles.modalSubtitle}>{selectedInterview.applicantName} - {selectedInterview.position}</Text>
              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>New Date</Text>
                <TextInput style={styles.textInput} placeholder="YYYY-MM-DD" placeholderTextColor="#9CA3AF"
                  defaultValue={selectedInterview.date ? new Date(selectedInterview.date).toISOString().split('T')[0] : ''} />
              </View>
              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>New Time</Text>
                <TextInput style={styles.textInput} placeholder="10:00 AM" placeholderTextColor="#9CA3AF"
                  defaultValue={selectedInterview.time || ''} />
              </View>
              <View style={styles.modalActions}>
                <TouchableOpacity style={styles.cancelButton} onPress={() => setShowRescheduleModal(false)}>
                  <Text style={styles.cancelButtonText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.saveButton} onPress={async () => {
                  try { await api.put(API_ENDPOINTS.interviews + '/' + selectedInterview.id, { status: 'scheduled', interview_date: new Date().toISOString() }); } catch {}
                  Alert.alert('Rescheduled', `Interview with ${selectedInterview.applicantName} rescheduled`);
                  setShowRescheduleModal(false);
                }}>
                  <Text style={styles.saveButtonText}>Confirm Reschedule</Text>
                </TouchableOpacity>
              </View>
            </ScrollView>}
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  header: {
    paddingTop: 8,
    paddingBottom: 12,
    paddingHorizontal: 16,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: 'white',
  },
  headerAction: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    borderRadius: 8,
    marginHorizontal: 16,
    marginTop: 6,
    marginBottom: 4,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    gap: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 13,
    color: '#0f172a',
    padding: 0,
    margin: 0,
  },
  statsContainer: {
    paddingHorizontal: 16,
    marginBottom: 4,
    paddingVertical: 4,
    flexGrow: 0,
    flexShrink: 0,
    maxHeight: 60,
  },
  statCard: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    paddingVertical: 6,
    paddingHorizontal: 14,
    marginRight: 8,
    minWidth: 60,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#e2e8f0',
    elevation: 1,
  },
  statValue: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#0f172a',
    lineHeight: 18,
  },
  statLabel: {
    fontSize: 9,
    color: '#64748B',
    textAlign: 'center',
    lineHeight: 12,
  },
  tabsContainer: {
    flexDirection: 'row',
    backgroundColor: '#ffffff',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
    flexGrow: 0,
    flexShrink: 0,
    height: 36,
  },
  tab: {
    flex: 1,
    paddingVertical: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  activeTab: {
    borderBottomWidth: 2,
    borderBottomColor: '#4A90E2',
  },
  tabText: {
    fontSize: 11,
    color: '#64748B',
    fontWeight: '500',
  },
  activeTabText: {
    color: '#4A90E2',
    fontWeight: '600',
  },
  listContainer: {
    paddingHorizontal: 20,
    paddingBottom: 100,
  },
  interviewCard: {
    backgroundColor: '#ffffff',
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  interviewTypeIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#f0f9ff',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  interviewInfo: {
    flex: 1,
  },
  applicantName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#0f172a',
    marginBottom: 2,
  },
  position: {
    fontSize: 14,
    color: '#4A90E2',
    marginBottom: 2,
  },
  interviewType: {
    fontSize: 12,
    color: '#64748B',
  },
  statusContainer: {
    alignItems: 'flex-end',
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 12,
    color: '#ffffff',
    fontWeight: '600',
  },
  cardDetails: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 12,
  },
  detailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    minWidth: '45%',
  },
  detailText: {
    fontSize: 12,
    color: '#64748B',
  },
  scoreContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
  },
  scoreLabel: {
    fontSize: 14,
    color: '#64748B',
  },
  scoreValue: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  cardActions: {
    flexDirection: 'row',
    gap: 8,
  },
  actionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#4A90E2',
    backgroundColor: '#ffffff',
    gap: 4,
  },
  primaryActionButton: {
    backgroundColor: '#4A90E2',
    borderColor: '#4A90E2',
  },
  dangerActionButton: {
    backgroundColor: '#EF4444',
    borderColor: '#EF4444',
  },
  actionButtonText: {
    fontSize: 12,
    color: '#4A90E2',
    fontWeight: '600',
  },
  fab: {
    position: 'absolute',
    bottom: 20,
    right: 20,
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#4A90E2',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
    paddingHorizontal: 40,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#64748B',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyDescription: {
    fontSize: 14,
    color: '#94A3B8',
    textAlign: 'center',
    lineHeight: 20,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f8f9fa',
  },
  loadingText: {
    fontSize: 16,
    color: '#64748B',
    marginTop: 16,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    backgroundColor: '#ffffff',
    borderRadius: 16,
    margin: 20,
    maxWidth: width * 0.9,
    maxHeight: height * 0.8,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#0f172a',
  },
  modalContent: {
    padding: 20,
  },
  modalSubtitle: {
    fontSize: 16,
    color: '#64748B',
    marginBottom: 20,
    textAlign: 'center',
  },
  inputGroup: {
    marginBottom: 16,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#0f172a',
    marginBottom: 8,
  },
  textInput: {
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    color: '#0f172a',
    backgroundColor: '#ffffff',
  },
  textArea: {
    height: 100,
    textAlignVertical: 'top',
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 20,
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: 16,
    color: '#64748B',
    fontWeight: '600',
  },
  saveButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: '#4A90E2',
    alignItems: 'center',
  },
  saveButtonText: {
    fontSize: 16,
    color: '#ffffff',
    fontWeight: '600',
  },
});

export default InterviewManagementScreen_Integrated;